import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { identifySuspiciousRatings } from '../utils/contentFilter';

// Provider URLs configuration (mirrors backend SOURCES)
const PROVIDER_SOURCES = {
  WATCHSERIES: {
    name: 'WatchSeries',
    baseUrl: 'https://watchseries.bar',
    moviePath: '/movie',
    tvPath: '/tv',
    type: 'Piracy'
  },
  GOODMOVIES: {
    name: 'GoodMoviesHD',
    baseUrl: 'https://goodmovieshd.com',
    moviePath: '/movies',
    tvPath: '/series',
    type: 'Piracy'
  },
  MOVIE123: {
    name: '0123Movie',
    baseUrl: 'https://ww20.0123movie.net',
    moviePath: '',
    tvPath: '',
    type: 'Piracy'
  },
  MEGASHAREMOVIE: {
    name: 'MegaShareMovie',
    baseUrl: 'https://megasharemovie.info',
    moviePath: '/featured-movies/',
    tvPath: '',
    type: 'Piracy'
  },
  PLUTOTV: {
    name: 'PlutoTV',
    baseUrl: 'https://pluto.tv',
    moviePath: '/on-demand/movies',
    tvPath: '/on-demand/series',
    type: 'Legal'
  },
  TUBITV: {
    name: 'TubiTV',
    baseUrl: 'https://tubitv.com',
    moviePath: ['/category/movies', '/category/featured'],
    tvPath: '/category/tv-shows',
    type: 'Legal'
  },
  PLEX: {
    name: 'Plex',
    baseUrl: 'https://watch.plex.tv',
    moviePath: ['/on-demand/free-to-watch', '/on-demand/movies'],
    tvPath: '/tv-shows',
    type: 'Legal'
  },
  ROKU: {
    name: 'RokuChannel',
    baseUrl: 'https://therokuchannel.roku.com',
    moviePath: ['/', '/browse/movies'],
    tvPath: '/browse/tv',
    type: 'Legal'
  },
  XUMO: {
    name: 'XumoPlay',
    baseUrl: 'https://play.xumo.com',
    moviePath: '/free-movies',
    tvPath: '/free-tv',
    type: 'Legal'
  },
  FREEVEE: {
    name: 'Amazon Freevee',
    baseUrl: 'https://www.amazon.com',
    moviePath: '/gp/video/movie',
    tvPath: '/gp/video/storefront/subscription/freewithads',
    type: 'Legal'
  },
  YOUTUBE: {
    name: 'YouTube Movies',
    baseUrl: 'https://www.youtube.com',
    moviePath: '/feed/storefront?bp=ogUCKAY%3D',
    tvPath: '/feed/storefront?bp=ogUCKAY%3D',
    type: 'Legal'
  }
};

// Helper function to validate poster URLs
const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  // Check if it's a properly formed URL
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const Debug = () => {
  const [activeTab, setActiveTab] = useState('edit');
  const [scrapedContent, setScrapedContent] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [firebaseMovies, setFirebaseMovies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseLoading, setFirebaseLoading] = useState(false);
  const [error, setError] = useState(null);
  const [firebaseError, setFirebaseError] = useState(null);
  const [backendStatus, setBackendStatus] = useState(null);

  // Manual scraping workflow state
  const [isScrapingManual, setIsScrapingManual] = useState(false);
  const [isSavingFirebase, setIsSavingFirebase] = useState(false);
  const [scrapeStatus, setScrapeStatus] = useState('');
  const [scrapedData, setScrapedData] = useState(null);
  const [scrapeBreakdown, setScrapeBreakdown] = useState(null);
  const [firebaseMoviesKey, setFirebaseMoviesKey] = useState(0);

  // Edit Movies tab state
  const [editMovies, setEditMovies] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [moviePage, setMoviePage] = useState(1);
  const [hasMoreMovies, setHasMoreMovies] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', type: '', posterUrl: '', year: '' });
  const [saveStatus, setSaveStatus] = useState('');
  const [refreshingId, setRefreshingId] = useState(null);

  // Batch refresh state
  const [batchRefreshing, setBatchRefreshing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  // Genres tab state
  const [genreData, setGenreData] = useState(null);
  const [genreLoading, setGenreLoading] = useState(false);
  const [cleanupStatus, setCleanupStatus] = useState('');
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState('');
  const [isBackfilling, setIsBackfilling] = useState(false);

  // Batch metadata refresh state
  const [batchRefreshStatus, setBatchRefreshStatus] = useState('');
  const [suspiciousMovies, setSuspiciousMovies] = useState([]);
  const [isBatchRefreshing, setIsBatchRefreshing] = useState(false);

  // Fix ALL state
  const [isFixingAll, setIsFixingAll] = useState(false);
  const [fixAllStatus, setFixAllStatus] = useState('');
  const [fixAllProgress, setFixAllProgress] = useState(null);

  // Provider URL editing state
  const [editableProviders, setEditableProviders] = useState(PROVIDER_SOURCES);
  const [editingProvider, setEditingProvider] = useState(null);
  const [newUrlPath, setNewUrlPath] = useState('');

  // Fetch backend status and scraped content on mount
  useEffect(() => {
    const checkBackendStatus = async () => {
      try {
        const data = await apiService.ping();
        setBackendStatus({
          status: data.status || 'OK',
          port: data.port || 'N/A',
          message: 'Connected'
        });
      } catch (err) {
        setBackendStatus({ status: 'Error', port: 'N/A', message: err.message });
      } finally {
        setLoading(false); // Set loading false after quick ping check
      }
    };

    const loadProviderConfigsFromDatabase = async () => {
      try {
        console.log('📖 Loading provider configurations from database...');
        const result = await apiService.loadProviderConfigs();
        
        if (result.success && result.count > 0) {
          console.log(`✅ Loaded ${result.count} provider configurations from database`);
          
          // Merge database configs with default configs
          const mergedProviders = { ...PROVIDER_SOURCES }; // Use constant to avoid dependency
          Object.keys(result.configs).forEach(providerKey => {
            const dbConfig = result.configs[providerKey];
            if (mergedProviders[providerKey]) {
              // Update existing provider with database URLs
              mergedProviders[providerKey] = {
                ...mergedProviders[providerKey],
                moviePath: dbConfig.moviePath
              };
              console.log(`🔄 Updated ${dbConfig.name} with database URLs:`, dbConfig.moviePath);
            }
          });
          
          setEditableProviders(mergedProviders);
          setScrapeStatus(`✅ Loaded ${result.count} provider configurations from database`);
          setTimeout(() => setScrapeStatus(''), 3000);
        } else {
          console.log('📖 No provider configurations found in database, using defaults');
        }
      } catch (error) {
        console.error('❌ Failed to load provider configs from database:', error);
        setScrapeStatus(`⚠️ Failed to load provider configs: ${error.message}`);
        setTimeout(() => setScrapeStatus(''), 5000);
      }
    };

    // Initialize provider URLs logging
    console.log('🔧 Debug component initialized with provider URLs:', Object.keys(PROVIDER_SOURCES).length, 'providers');
    console.log('🔧 Initial Plex URLs:', PROVIDER_SOURCES.PLEX?.moviePath);

    checkBackendStatus();
    loadProviderConfigsFromDatabase();
  }, []);

  // Fetch scraped content only when status or scraped tabs are accessed
  useEffect(() => {
    if ((activeTab === 'status' || activeTab === 'scraped') && !scrapedContent) {
      fetchScrapedContent();
    }
  }, [activeTab, scrapedContent]);

  // Fetch Firebase movies when Firebase tab is activated
  useEffect(() => {
    if (activeTab === 'firebase' && !firebaseMovies && !firebaseLoading) {
      fetchFirebaseMovies();
    }
  }, [activeTab]);

  // Fetch movies for edit tab
  useEffect(() => {
    if (activeTab === 'edit' && !editMovies && !editLoading) {
      fetchEditMovies();
    }
  }, [activeTab, editMovies, editLoading]);

  // Fetch genre debug data when Genres or Edit Movies tabs are activated
  useEffect(() => {
    if ((activeTab === 'genres' || activeTab === 'edit') && !genreData && !genreLoading) {
      fetchGenreDebugData();
    }
  }, [activeTab, genreData, genreLoading]);

  const fetchScrapedContent = async () => {
    try {
      const response = await apiService.getScrapedContent();
      setScrapedContent(response.data || response);
      setDebugInfo(response.debug || {});
    } catch (err) {
      console.error('Error fetching scraped content:', err);
      setError(err);
    }
  };

  const fetchFirebaseMovies = async () => {
    setFirebaseLoading(true);
    setFirebaseError(null);
    try {
      const response = await apiService.getFirebaseMovies(100); // Start with 100 for Firebase tab
      setFirebaseMovies(response.movies);
    } catch (err) {
      console.error('Error fetching Firebase movies:', err);
      setFirebaseError(err);
    } finally {
      setFirebaseLoading(false);
    }
  };

  // Manual scraping workflow handlers
  const handleScrapeNow = async () => {
    setIsScrapingManual(true);
    setScrapeStatus('🔧 Syncing provider URLs...');
    
    try {
      // Step 1: Sync all provider URLs with backend before scraping
      console.log('🔧 Pre-scrape: Syncing all provider URLs with backend...');
      console.log('🔧 Current frontend provider state:', Object.fromEntries(
        Object.keys(editableProviders).map(key => [
          editableProviders[key].name,
          editableProviders[key].moviePath
        ])
      ));
      
      // Instead of using separate PUT requests (which run in different serverless instances),
      // we'll send all provider URLs with the scrape request to ensure state consistency
      const providerUrlsForScrape = {};
      Object.keys(editableProviders).forEach(providerKey => {
        const providerName = editableProviders[providerKey].name;
        const currentUrls = Array.isArray(editableProviders[providerKey].moviePath) 
          ? editableProviders[providerKey].moviePath 
          : [editableProviders[providerKey].moviePath];
        
        providerUrlsForScrape[providerName] = currentUrls;
        console.log(`🔧 Will sync ${providerName} during scrape:`, currentUrls);
      });
      
      console.log(`🔧 Provider URLs to sync with scrape request:`, providerUrlsForScrape);
      
      // Step 2: Start the actual scraping with provider URLs included
      setScrapeStatus('🔍 Scraping content...');
      const response = await apiService.triggerScrape(providerUrlsForScrape);
      console.log('DEBUG: Full scrape response:', response);
      console.log('DEBUG: Breakdown data:', response.breakdown);
      console.log('DEBUG: API Debug Info:', response.debugInfo);
      
      // Force display of debug info in UI
      if (response.debugInfo) {
        console.log('🚀 API Debug Info:', {
          functionCalled: response.debugInfo.functionCalled,
          resultType: response.debugInfo.resultType,
          resultKeys: response.debugInfo.resultKeys,
          hasBreakdown: response.debugInfo.hasBreakdown,
          breakdownType: response.debugInfo.breakdownType
        });
      }
      setScrapedData(response.data);
      setScrapeBreakdown(response.breakdown);
      
      // Create fallback breakdown if backend doesn't provide it
      if (!response.breakdown && response.data && response.summary) {
        const fallbackBreakdown = {
          totalMovies: response.summary.totalMovies,
          totalSources: Object.keys(response.data).length,
          providers: {}
        };
        
        Object.entries(response.data).forEach(([sourceName, movies]) => {
          fallbackBreakdown.providers[sourceName] = {
            name: sourceName,
            movieCount: movies.length,
            status: movies.length > 0 ? 'success' : 'failed',
            urls: [{
              path: 'main',
              movieCount: movies.length,
              status: movies.length > 0 ? 'success' : 'failed'
            }]
          };
        });
        
        console.log('DEBUG: Using fallback breakdown:', fallbackBreakdown);
        setScrapeBreakdown(fallbackBreakdown);
      }
      
      setScrapeStatus(`✅ Scraped ${response.summary.totalMovies} movies from ${Object.keys(response.data).length} sources`);
      setScrapedContent(response.data); // Update display
      setDebugInfo(response.summary); // Update debug info
    } catch (error) {
      console.error('Scrape error:', error);
      setScrapeStatus(`❌ Scrape error: ${error.message}`);
    } finally {
      setIsScrapingManual(false);
    }
  };

  const handleSaveToFirebase = async () => {
    if (!scrapedData) {
      setScrapeStatus('⚠️ Please scrape first!');
      return;
    }

    setIsSavingFirebase(true);
    
    try {
      // Calculate total movies across all sources
      const totalMovies = Object.keys(scrapedData).reduce((total, sourceName) => {
        return total + (Array.isArray(scrapedData[sourceName]) ? scrapedData[sourceName].length : 0);
      }, 0);
      
      console.log(`🔄 Starting batch save for ${totalMovies} movies...`);
      
      // Batch configuration
      const BATCH_SIZE = 25; // Process 25 movies per batch to stay under 10s timeout
      const totalBatches = Math.ceil(totalMovies / BATCH_SIZE);
      
      console.log(`📦 Splitting into ${totalBatches} batches of ${BATCH_SIZE} movies each`);
      
      let totalResults = {
        created: 0,
        updated: 0,
        errors: 0,
        tmdbEnriched: 0,
        tmdbFailed: 0
      };
      
      // Process each batch
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        setScrapeStatus(`💾 Saving batch ${batchIndex + 1}/${totalBatches}... (${Math.min((batchIndex + 1) * BATCH_SIZE, totalMovies)}/${totalMovies} movies)`);
        
        try {
          const response = await apiService.saveScrapedToFirebase(scrapedData, {
            batchIndex,
            batchSize: BATCH_SIZE,
            totalBatches
          });
          
          // Accumulate results
          totalResults.created += response.results.created;
          totalResults.updated += response.results.updated;
          totalResults.errors += response.results.errors;
          totalResults.tmdbEnriched += response.results.tmdbEnriched;
          totalResults.tmdbFailed += response.results.tmdbFailed;
          
          console.log(`✅ Batch ${batchIndex + 1}/${totalBatches} complete:`, response.results);
          
        } catch (batchError) {
          console.error(`❌ Batch ${batchIndex + 1} failed:`, batchError.message);
          totalResults.errors += BATCH_SIZE; // Assume all movies in batch failed
          
          // Continue with next batch even if one fails
          setScrapeStatus(`⚠️ Batch ${batchIndex + 1} failed, continuing with next batch...`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Brief pause before retry
        }
      }
      
      // Final summary
      setScrapeStatus(`✅ All batches complete! ${totalResults.created} new, ${totalResults.updated} updated, ${totalResults.errors} errors, ${totalResults.tmdbEnriched} TMDB enriched`);
      setFirebaseMoviesKey(prev => prev + 1); // Trigger Firebase tab refresh
      
      // Refresh Edit Movies data to show newly saved movies
      fetchEditMovies();
      
      // Refresh Genre Statistics to show updated counts
      fetchGenreDebugData();
      
    } catch (error) {
      console.error('Batch save error:', error);
      setScrapeStatus(`❌ Save error: ${error.message}`);
    } finally {
      setIsSavingFirebase(false);
    }
  };

  // Edit Movies handlers
  const fetchEditMovies = async () => {
    setEditLoading(true);
    setMoviePage(1);
    setHasMoreMovies(true);
    try {
      const response = await apiService.getFirebaseMovies(100);
      const movies = response.movies;
      console.log('Fetched movies for edit:', movies);
      // Sort movies alphabetically by title
      const sortedMovies = movies.sort((a, b) => a.title.localeCompare(b.title));
      setEditMovies(sortedMovies);
      setHasMoreMovies(response.hasMore);
    } catch (error) {
      console.error('Error fetching movies for editing:', error);
    } finally {
      setEditLoading(false);
    }
  };

  const loadMoreMovies = async () => {
    setEditLoading(true);
    try {
      const offset = editMovies.length;
      console.log(`[LoadMore] Loading movies with offset: ${offset}`);
      const response = await apiService.getFirebaseMovies(100, offset);
      const moreMovies = response.movies;
      console.log(`[LoadMore] Loaded ${moreMovies.length} new movies (total: ${editMovies.length + moreMovies.length})`);
      
      // Only add movies if we got new ones (avoid duplicates)
      if (moreMovies.length > 0) {
        // Combine and sort alphabetically
        setEditMovies(prev => [...prev, ...moreMovies].sort((a, b) => a.title.localeCompare(b.title)));
        setMoviePage(prev => prev + 1);
      }
      
      // Stop pagination if we got fewer movies than requested
      setHasMoreMovies(response.hasMore);
    } catch (error) {
      console.error('Error loading more movies:', error);
      setHasMoreMovies(false);
    } finally {
      setEditLoading(false);
    }
  };

  const startEdit = (movie) => {
    setEditingId(movie.id);
    setEditForm({
      title: movie.title || '',
      type: movie.type || 'movie',
      posterUrl: movie.metadata?.posterUrl || '',
      year: movie.metadata?.year || ''
    });
    setSaveStatus('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: '', type: '', posterUrl: '', year: '' });
    setSaveStatus('');
  };

  const saveEdit = async (movieId) => {
    setSaveStatus('Saving...');
    try {
      await apiService.updateFirebaseMovie(movieId, editForm);

      // Update local state
      setEditMovies(prev => prev.map(m =>
        m.id === movieId
          ? {
              ...m,
              title: editForm.title,
              type: editForm.type,
              metadata: { ...m.metadata, posterUrl: editForm.posterUrl }
            }
          : m
      ));

      setSaveStatus('✅ Saved!');
      setEditingId(null);

      // Clear success message after 2 seconds
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (error) {
      console.error('Error saving movie:', error);
      setSaveStatus(`❌ Error: ${error.message}`);
    }
  };

  const handleDelete = async (movie) => {
    // Show confirmation dialog
    const confirmed = window.confirm(`Are you sure you want to delete "${movie.title}"?\n\nThis action cannot be undone.`);

    if (!confirmed) {
      return;
    }

    setSaveStatus('Deleting...');
    try {
      await apiService.deleteFirebaseMovie(movie.id);

      // Remove from local state
      setEditMovies(prev => prev.filter(m => m.id !== movie.id));

      setSaveStatus(`✅ Deleted "${movie.title}"`);

      // Clear success message after 3 seconds
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error deleting movie:', error);
      setSaveStatus(`❌ Delete error: ${error.message}`);
    }
  };

  const handleRefreshMetadata = async (movieId) => {
    const movie = editMovies.find(m => m.id === movieId);
    const movieTitle = movie?.title || 'Unknown Movie';
    
    setRefreshingId(movieId);
    setSaveStatus(`🔍 Searching TMDB for "${movieTitle}"...`);
    
    try {
      console.log(`[DEBUG] Starting enhanced refresh for "${movieTitle}" (ID: ${movieId})`);
      
      // Enhanced status updates during the process
      setSaveStatus(`🎬 Fetching metadata for "${movieTitle}"...`);
      
      const updatedMovie = await apiService.refreshMetadata(movieId);
      
      const isPlaceholderUrl = updatedMovie.metadata?.posterUrl?.includes('via.placeholder.com');
      const hasRealPoster = updatedMovie.metadata?.posterUrl && !isPlaceholderUrl;
      
      console.log(`[DEBUG] Enhanced refresh completed:`, {
        movieId,
        title: movieTitle,
        updatedGenres: updatedMovie.metadata?.genres,
        hasNewPoster: !!updatedMovie.metadata?.posterUrl,
        hasRealPoster: hasRealPoster,
        isPlaceholder: isPlaceholderUrl,
        actualPosterUrl: updatedMovie.metadata?.posterUrl,
        editingId: editingId,
        isCurrentlyEditing: editingId === movieId
      });
      
      // Update the movie in the list with enhanced data handling
      setEditMovies(prev => prev.map(m => 
        m.id === movieId ? {
          ...m,
          ...updatedMovie,
          genre: updatedMovie.metadata?.genres || updatedMovie.genre || m.genre
        } : m
      ));

      // Always refresh the movie data from database to show updated poster
      console.log(`[DEBUG] Fetching updated movie data from database to reflect changes...`);
      // Add small delay to ensure database write completes
      console.log(`[DEBUG] Waiting 1 second for database write to complete...`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      try {
        const currentCount = editMovies?.length || 100;
        const response = await apiService.getFirebaseMovies(currentCount);
        const refreshedMovies = response.movies;
        // Sort alphabetically after refresh
        const sortedMovies = refreshedMovies.sort((a, b) => a.title.localeCompare(b.title));
        setEditMovies(sortedMovies);
        console.log(`[DEBUG] Movie list refreshed from database`);

        // Check the specific movie we just updated
        const updatedMovie = refreshedMovies.find(m => m.id === movieId);
        if (updatedMovie) {
          console.log(`[DEBUG] Specific movie after refresh:`, {
            id: updatedMovie.id,
            title: updatedMovie.title,
            posterUrl: updatedMovie.metadata?.posterUrl,
            isStillPlaceholder: updatedMovie.metadata?.posterUrl?.includes('via.placeholder.com')
          });
        } else {
          console.error(`[DEBUG] Could not find updated movie ${movieId} in refreshed list`);
        }

        // If currently editing, also update the edit form
        if (editingId === movieId) {
          const updatedMovie = refreshedMovies.find(m => m.id === movieId);
          if (updatedMovie) {
            console.log(`[DEBUG] Updating edit form with refreshed poster URL:`, updatedMovie.metadata?.posterUrl);
            setEditForm(prev => ({
              ...prev,
              posterUrl: updatedMovie.metadata?.posterUrl || ''
            }));
            console.log(`[DEBUG] Edit form updated with database data`);
          }
        } else {
          // Even if not actively editing, update edit form if it contains a movie that was just refreshed
          if (editForm.posterUrl && editForm.posterUrl.includes('placeholder')) {
            const refreshedMovie = refreshedMovies.find(m => m.id === movieId);
            if (refreshedMovie && refreshedMovie.metadata?.posterUrl && !refreshedMovie.metadata.posterUrl.includes('placeholder')) {
              console.log(`[DEBUG] Syncing edit form with refreshed poster URL for non-editing mode:`, refreshedMovie.metadata.posterUrl);
              setEditForm(prev => ({
                ...prev,
                posterUrl: refreshedMovie.metadata.posterUrl
              }));
            }
          }
        }
      } catch (error) {
        console.error(`[DEBUG] Failed to refresh movie data:`, error);
      }
      
      // Enhanced success message with details
      const genreCount = updatedMovie.metadata?.genres?.length || 0;
      const successMessage = genreCount > 0 
        ? `✅ Refreshed "${movieTitle}" with ${genreCount} genres!`
        : `✅ Refreshed "${movieTitle}"!`;
      
      setSaveStatus(successMessage);
      setTimeout(() => setSaveStatus(''), 3000); // Slightly longer display time
      
    } catch (error) {
      console.error(`[DEBUG] Enhanced refresh failed for "${movieTitle}":`, error);
      
      // Enhanced error handling with more specific messages
      let errorMessage = `❌ Refresh failed for "${movieTitle}"`;
      
      if (error.message.includes('not found') || error.message.includes('NOT_FOUND')) {
        errorMessage = `❌ "${movieTitle}" not found on TMDB`;
      } else if (error.message.includes('network') || error.message.includes('timeout')) {
        errorMessage = `❌ Network error refreshing "${movieTitle}"`;
      } else if (error.message.includes('DATABASE_UPDATE_FAILED')) {
        errorMessage = `❌ Database update failed for "${movieTitle}"`;
      } else if (error.message) {
        errorMessage = `❌ ${error.message}`;
      }
      
      setSaveStatus(errorMessage);
      
      // Auto-clear error messages after longer time for reading
      setTimeout(() => setSaveStatus(''), 5000);
      
    } finally {
      setRefreshingId(null);
    }
  };

  // Smart detection for movies with likely fake ratings
  const detectMoviesWithFakeRatings = () => {
    if (!editMovies) return [];

    return editMovies.filter(movie => {
      // Check for ratings in the old fallback range (6.5-9.0)
      const imdbRating = parseFloat(movie.imdbRating || movie.ratings?.imdbRating || 0);
      const tmdbRating = parseFloat(movie.tmdbRating || movie.ratings?.tmdbRating || 0);
      
      // Check if rating is in suspicious range
      const suspiciousImdb = imdbRating >= 6.5 && imdbRating <= 9.0;
      const suspiciousTmdb = tmdbRating >= 6.5 && tmdbRating <= 9.0;
      
      // Check for minimal metadata (indicators of fake data)
      const hasMinimalMetadata = !movie.metadata?.plot || 
                                movie.metadata?.plot === null ||
                                !movie.metadata?.actors?.length ||
                                movie.metadata?.actors?.length === 0;
      
      // Movie likely has fake rating if it has suspicious rating + minimal metadata
      return (suspiciousImdb || suspiciousTmdb) && hasMinimalMetadata;
    });
  };

  // Batch refresh movies with fake ratings
  const handleBatchRefreshFakeRatings = async () => {
    const moviesWithFakeRatings = detectMoviesWithFakeRatings();
    
    if (moviesWithFakeRatings.length === 0) {
      setSaveStatus('✅ No movies with fake ratings detected!');
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }

    setBatchRefreshing(true);
    setBatchProgress({ current: 0, total: moviesWithFakeRatings.length });
    
    console.log(`[DEBUG] Starting batch refresh for ${moviesWithFakeRatings.length} movies with likely fake ratings`);
    
    try {
      for (let i = 0; i < moviesWithFakeRatings.length; i++) {
        const movie = moviesWithFakeRatings[i];
        setBatchProgress({ current: i + 1, total: moviesWithFakeRatings.length });
        setSaveStatus(`🔄 Refreshing ${i + 1}/${moviesWithFakeRatings.length}: "${movie.title}"`);
        
        try {
          await apiService.refreshMetadata(movie.id);
          console.log(`[DEBUG] Successfully refreshed metadata for: ${movie.title}`);
        } catch (error) {
          console.error(`[DEBUG] Failed to refresh ${movie.title}:`, error);
        }
        
        // Small delay to avoid overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Refresh the movie list to show updated data (maintain current page size)
      setSaveStatus('🔄 Reloading updated movie list...');
      const currentCount = editMovies?.length || 100;
      const response = await apiService.getFirebaseMovies(currentCount);
      const refreshedMovies = response.movies;
      // Sort alphabetically after batch refresh
      const sortedMovies = refreshedMovies.sort((a, b) => a.title.localeCompare(b.title));
      setEditMovies(sortedMovies);
      
      setSaveStatus(`✅ Successfully refreshed ${moviesWithFakeRatings.length} movies with fake ratings!`);
      setTimeout(() => setSaveStatus(''), 5000);
      
    } catch (error) {
      console.error('[DEBUG] Batch refresh failed:', error);
      setSaveStatus(`❌ Batch refresh failed: ${error.message}`);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setBatchRefreshing(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  // Scan for movies with suspicious ratings
  const handleScanSuspiciousRatings = () => {
    console.log('🔍 [Debug] handleScanSuspiciousRatings called!');
    console.log('🔍 [Debug] firebaseMovies state:', firebaseMovies);
    console.log('🔍 [Debug] firebaseMovies length:', firebaseMovies?.length);
    
    if (!firebaseMovies || firebaseMovies.length === 0) {
      console.log('❌ [Debug] No movies loaded, setting error status');
      setBatchRefreshStatus('❌ No movies loaded. Please wait for Firebase data to load.');
      return;
    }

    console.log('🔍 [Debug] Calling identifySuspiciousRatings...');
    const suspicious = identifySuspiciousRatings(firebaseMovies);
    console.log('🔍 [Debug] Found suspicious movies:', suspicious);
    
    setSuspiciousMovies(suspicious);
    setBatchRefreshStatus(`🔍 Found ${suspicious.length} movies with suspicious ratings`);
    console.log('🔍 [Debug] Status updated!');
  };

  // Batch refresh suspicious movies
  const handleBatchRefreshSuspicious = async () => {
    if (suspiciousMovies.length === 0) {
      setBatchRefreshStatus('❌ No suspicious movies to refresh. Run scan first.');
      return;
    }

    setIsBatchRefreshing(true);
    setBatchRefreshStatus(`🔄 Starting batch refresh of ${suspiciousMovies.length} movies...`);

    try {
      const movieIds = suspiciousMovies.map(movie => movie.id);
      
      const results = await apiService.batchRefreshMetadata(movieIds, (progress) => {
        setBatchRefreshStatus(`🔄 ${progress.current}/${progress.total} - Processing: ${progress.movieId}`);
      });

      setBatchRefreshStatus(
        `✅ Batch refresh complete! ` +
        `${results.successful} successful, ${results.failed} failed ` +
        `(${Math.round(results.successful / results.total * 100)}% success rate)`
      );

      // Clear suspicious list after successful batch refresh
      setSuspiciousMovies([]);

      // Refresh the movie list to show updated data
      if (results.successful > 0) {
        setTimeout(() => {
          window.location.reload(); // Simple way to refresh all data
        }, 2000);
      }

    } catch (error) {
      console.error('❌ Batch refresh failed:', error);
      setBatchRefreshStatus(`❌ Batch refresh failed: ${error.message}`);
    } finally {
      setIsBatchRefreshing(false);
    }
  };

  // Fix ALL movies - progressive scan and fix
  const handleFixAll = async () => {
    setIsFixingAll(true);
    setFixAllStatus('🚀 Starting comprehensive database scan...');
    setFixAllProgress(null);

    try {
      const results = await apiService.fixAllMoviesProgressive((progress) => {
        setFixAllProgress(progress);
        setFixAllStatus(progress.status);
        console.log(`[Fix ALL] Phase: ${progress.phase}, Status: ${progress.status}`);
      });

      // Final results summary
      const summary = `✅ Fix ALL Complete!\n` +
        `📊 Total Movies: ${results.totalMovies}\n` +
        `🔍 Scanned: ${results.totalScanned}\n` +
        `⚠️ Suspicious Found: ${results.totalSuspicious}\n` +
        `✅ Successfully Fixed: ${results.totalFixed}\n` +
        `❌ Failed to Fix: ${results.totalFailed}`;

      setFixAllStatus(summary);
      console.log(`[Fix ALL] Final Results:`, results);

      // Refresh the page after successful completion if any movies were fixed
      if (results.totalFixed > 0) {
        setTimeout(() => {
          if (window.confirm(`Fix ALL completed successfully!\n\n${summary}\n\nRefresh the page to see updated data?`)) {
            window.location.reload();
          }
        }, 3000);
      }

    } catch (error) {
      console.error('❌ Fix ALL failed:', error);
      setFixAllStatus(`❌ Fix ALL failed: ${error.message}`);
    } finally {
      setIsFixingAll(false);
    }
  };

  // Genres Debug Data Fetching
  const fetchGenreDebugData = async () => {
    setGenreLoading(true);
    try {
      const response = await apiService.getFirebaseMovies(100); // Start with 100 for genre stats
      const movies = response.movies;
      const analysis = analyzeGenreData(movies);
      setGenreData(analysis);
    } catch (error) {
      console.error('Genre debug fetch failed:', error);
      setGenreData({ error: error.message });
    } finally {
      setGenreLoading(false);
    }
  };

  const analyzeGenreData = (movies) => {
    // Statistics
    const withGenres = movies.filter(m => m.genre?.length > 0 || m.metadata?.genres?.length > 0);
    const withRootGenre = movies.filter(m => m.genre?.length > 0);
    const withMetadataGenres = movies.filter(m => m.metadata?.genres?.length > 0);
    const withBoth = movies.filter(m => m.genre?.length > 0 && m.metadata?.genres?.length > 0);
    const withEmptyGenreArray = movies.filter(m => Array.isArray(m.genre) && m.genre.length === 0);

    // Genre counts
    const genreCounts = {};
    movies.forEach(m => {
      const genres = m.genre || m.metadata?.genres || [];
      genres.forEach(g => {
        genreCounts[g] = (genreCounts[g] || 0) + 1;
      });
    });

    return {
      total: movies.length,
      stats: {
        withGenres: withGenres.length,
        withRootGenre: withRootGenre.length,
        withMetadataGenres: withMetadataGenres.length,
        withBoth: withBoth.length,
        withEmptyGenreArray: withEmptyGenreArray.length,
        noGenres: movies.length - withGenres.length
      },
      genreCounts: Object.entries(genreCounts).sort((a, b) => b[1] - a[1]),
      samples: movies.slice(0, 15).map(m => ({
        title: m.title,
        id: m.id,
        genre: m.genre || [],
        metadataGenres: m.metadata?.genres || [],
        hasRootGenre: !!m.genre?.length,
        hasMetadataGenres: !!m.metadata?.genres?.length,
        hasEmptyRootGenre: Array.isArray(m.genre) && m.genre.length === 0
      }))
    };
  };

  const handleCleanupDuplicates = async () => {
    setIsCleaningUp(true);
    setCleanupStatus('🧹 Cleaning up duplicates...');
    try {
      const response = await apiService.cleanupDuplicates();
      setCleanupStatus(`✅ Cleanup complete! Kept: ${response.results.kept}, Deleted: ${response.results.deleted}, Errors: ${response.results.errors}`);

      // Refresh genre data after cleanup
      fetchGenreDebugData();
    } catch (error) {
      console.error('Cleanup error:', error);
      setCleanupStatus(`❌ Cleanup error: ${error.message}`);
    } finally {
      setIsCleaningUp(false);
    }
  };

  const handleBackfillGenres = async () => {
    setIsBackfilling(true);
    setBackfillStatus('Backfilling genres...');
    try {
      const response = await apiService.backfillGenres();
      setBackfillStatus(`✅ Backfill complete! Updated: ${response.results.updated}, Errors: ${response.results.errors}`);
      fetchGenreDebugData(); // Refresh genre data
    } catch (error) {
      console.error('Backfill error:', error);
      setBackfillStatus(`❌ Backfill error: ${error.message}`);
    } finally {
      setIsBackfilling(false);
    }
  };

  // Tab navigation styles
  const tabStyle = (tabName) => ({
    padding: '12px 24px',
    margin: '0 5px',
    background: activeTab === tabName ? '#e50914' : '#333',
    color: 'white',
    border: 'none',
    borderRadius: '5px 5px 0 0',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: activeTab === tabName ? 'bold' : 'normal',
    transition: 'all 0.2s'
  });

  // Helper functions for URL editing
  // Validate URLs before syncing
  const validateProviderUrls = (providerKey, urls) => {
    const provider = editableProviders[providerKey];
    const issues = [];
    
    // Check for empty URLs
    if (urls.some(url => !url || url.trim() === '')) {
      issues.push('Contains empty URLs');
    }
    
    // Check for URLs that don't start with /
    if (urls.some(url => url && !url.startsWith('/'))) {
      issues.push('URLs must start with /');
    }
    
    // Provider-specific validations
    if (providerKey === 'FREEVEE' && urls.some(url => url.includes('storefront/subscription') && !url.includes('movie'))) {
      issues.push('Amazon Freevee URLs should use /gp/video/movie for movies (not subscription pages)');
    }
    
    return issues;
  };

  // Sync provider URLs with backend
  const syncProviderUrls = async (providerKey) => {
    try {
      const provider = editableProviders[providerKey];
      const providerName = provider.name;
      const urls = Array.isArray(provider.moviePath) ? provider.moviePath : [provider.moviePath];
      
      // Validate URLs before syncing
      const validationIssues = validateProviderUrls(providerKey, urls);
      if (validationIssues.length > 0) {
        console.warn(`⚠️  URL validation warnings for ${providerName}:`, validationIssues);
        // Continue anyway, but log warnings
      }
      
      console.log(`🔧 Syncing ${providerName} URLs with backend:`, urls);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/scrape`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          providerName,
          urls,
          providerConfig: provider // Include full provider config for database save
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to sync URLs');
      }
      
      console.log(`✅ Successfully synced ${providerName} URLs with backend`);
      return result;
    } catch (error) {
      console.error(`❌ Failed to sync ${providerKey} URLs:`, error);
      throw error;
    }
  };

  const addUrlToProvider = async (providerKey, newPath) => {
    const providerName = editableProviders[providerKey].name;
    
    setEditableProviders(prev => {
      const updated = { ...prev };
      const provider = updated[providerKey];
      
      if (Array.isArray(provider.moviePath)) {
        provider.moviePath = [...provider.moviePath, newPath];
      } else {
        provider.moviePath = [provider.moviePath, newPath];
      }
      
      return updated;
    });
    setNewUrlPath('');
    
    // Sync with backend and provide user feedback
    try {
      setScrapeStatus(`🔧 Adding URL to ${providerName}...`);
      const result = await syncProviderUrls(providerKey);
      
      if (result && result.success) {
        const dbStatus = result.savedToDatabase ? 'and saved to database' : 
                        result.databaseError ? '(database save failed)' : '(in-memory only)';
        setScrapeStatus(`✅ URL added to ${providerName}: ${newPath} ${dbStatus}`);
        console.log(`✅ URL successfully added to ${providerName}:`, result.urls, 'Database saved:', result.savedToDatabase);
      } else {
        throw new Error(result?.error || 'Sync failed');
      }
      
      // Clear status after 3 seconds
      setTimeout(() => setScrapeStatus(''), 3000);
      
    } catch (error) {
      console.error('Failed to sync URL addition with backend:', error);
      setScrapeStatus(`❌ Failed to add URL to ${providerName}: ${error.message}`);
      
      // Revert the frontend change since sync failed
      setEditableProviders(prev => {
        const reverted = { ...prev };
        const provider = reverted[providerKey];
        if (Array.isArray(provider.moviePath)) {
          provider.moviePath = provider.moviePath.filter(url => url !== newPath);
        }
        return reverted;
      });
      
      // Clear error status after 5 seconds
      setTimeout(() => setScrapeStatus(''), 5000);
    }
  };

  const removeUrlFromProvider = async (providerKey, pathIndex) => {
    const providerName = editableProviders[providerKey].name;
    const provider = editableProviders[providerKey];
    const urlToRemove = Array.isArray(provider.moviePath) ? provider.moviePath[pathIndex] : provider.moviePath;
    
    // Don't remove if only one URL left
    if (Array.isArray(provider.moviePath) && provider.moviePath.length <= 1) {
      setScrapeStatus(`⚠️ Cannot remove last URL from ${providerName}`);
      setTimeout(() => setScrapeStatus(''), 3000);
      return;
    }
    
    setEditableProviders(prev => {
      const updated = { ...prev };
      const provider = updated[providerKey];
      
      if (Array.isArray(provider.moviePath)) {
        provider.moviePath = provider.moviePath.filter((_, index) => index !== pathIndex);
      }
      
      return updated;
    });
    
    // Sync with backend and provide user feedback
    try {
      setScrapeStatus(`🔧 Removing URL from ${providerName}...`);
      const result = await syncProviderUrls(providerKey);
      
      if (result && result.success) {
        const dbStatus = result.savedToDatabase ? 'and updated in database' : 
                        result.databaseError ? '(database update failed)' : '(in-memory only)';
        setScrapeStatus(`✅ URL removed from ${providerName}: ${urlToRemove} ${dbStatus}`);
        console.log(`✅ URL successfully removed from ${providerName}:`, result.urls, 'Database updated:', result.savedToDatabase);
      } else {
        throw new Error(result?.error || 'Sync failed');
      }
      
      // Clear status after 3 seconds
      setTimeout(() => setScrapeStatus(''), 3000);
      
    } catch (error) {
      console.error('Failed to sync URL removal with backend:', error);
      setScrapeStatus(`❌ Failed to remove URL from ${providerName}: ${error.message}`);
      
      // Revert the frontend change since sync failed
      setEditableProviders(prev => {
        const reverted = { ...prev };
        const provider = reverted[providerKey];
        if (Array.isArray(provider.moviePath)) {
          provider.moviePath = [...provider.moviePath];
          provider.moviePath.splice(pathIndex, 0, urlToRemove); // Re-insert at original position
        }
        return reverted;
      });
      
      // Clear error status after 5 seconds
      setTimeout(() => setScrapeStatus(''), 5000);
    }
  };

  const tabContainerStyle = {
    borderBottom: '2px solid #444',
    marginBottom: '20px',
    display: 'flex'
  };

  // Helper function to render Genre Statistics (reusable in multiple tabs)
  const renderGenreStatistics = () => {
    if (!genreData || genreData.error) {
      return null;
    }

    return (
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '15px' }}>📊 Genre Statistics</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '15px'
        }}>
          <div style={{
            background: '#1a1a1a',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #444',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#e50914' }}>
              {genreData.total}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
              Total Movies
            </div>
          </div>
          <div style={{
            background: '#1a1a1a',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #444',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#28a745' }}>
              {genreData.stats.withGenres}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
              With Genres
            </div>
          </div>
          <div style={{
            background: '#1a1a1a',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #444',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f44336' }}>
              {genreData.stats.noGenres}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
              No Genres
            </div>
          </div>
          <div style={{
            background: '#1a1a1a',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #444',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#4a9eff' }}>
              {genreData.stats.withRootGenre}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
              Has 'genre' Field
            </div>
          </div>
          <div style={{
            background: '#1a1a1a',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #444',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ffa500' }}>
              {genreData.stats.withMetadataGenres}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
              Has 'metadata.genres'
            </div>
          </div>
          <div style={{
            background: '#1a1a1a',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #444',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ff6b6b' }}>
              {genreData.stats.withEmptyGenreArray}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
              Empty genre[] Arrays
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div style={{ padding: '20px', color: 'white' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '20px', color: 'white', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '20px' }}>Debug Console</h1>

      {/* Tab Navigation */}
      <div style={tabContainerStyle}>
        <button style={tabStyle('status')} onClick={() => setActiveTab('status')}>
          Backend Status
        </button>
        <button style={tabStyle('scraped')} onClick={() => setActiveTab('scraped')}>
          Scraped Content
        </button>
        <button style={tabStyle('providers')} onClick={() => setActiveTab('providers')}>
          Provider URLs
        </button>
        <button style={tabStyle('firebase')} onClick={() => setActiveTab('firebase')}>
          Firebase Movies
        </button>
        <button style={tabStyle('edit')} onClick={() => setActiveTab('edit')}>
          Edit Movies
        </button>
        <button style={tabStyle('genres')} onClick={() => setActiveTab('genres')}>
          Genres
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {/* Backend Status Tab */}
        {activeTab === 'status' && (
          <div>
            <h2>Backend Status</h2>
            {backendStatus ? (
              <div style={{
                background: '#1a1a1a',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #444'
              }}>
                <p><strong>Status:</strong> <span style={{ color: backendStatus.status === 'OK' ? '#4caf50' : '#f44336' }}>{backendStatus.status}</span></p>
                <p><strong>Port:</strong> {backendStatus.port}</p>
                <p><strong>Message:</strong> {backendStatus.message}</p>
              </div>
            ) : (
              <p>Checking backend status...</p>
            )}
          </div>
        )}

        {/* Scraped Content Tab */}
        {activeTab === 'scraped' && (
          <div>
            <h2>Scraped Content by Source</h2>

            {/* Manual Scraping Controls */}
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              background: '#1a1a1a',
              borderRadius: '8px',
              border: '1px solid #444'
            }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button
                  onClick={handleScrapeNow}
                  disabled={isScrapingManual}
                  style={{
                    padding: '10px 20px',
                    background: isScrapingManual ? '#666' : '#e50914',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isScrapingManual ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {isScrapingManual ? '🔄 Scraping...' : '🔄 Scrape Now'}
                </button>

                <button
                  onClick={handleSaveToFirebase}
                  disabled={isSavingFirebase || !scrapedData}
                  style={{
                    padding: '10px 20px',
                    background: isSavingFirebase || !scrapedData ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isSavingFirebase || !scrapedData ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {isSavingFirebase ? '💾 Saving...' : '💾 Save to Firebase'}
                </button>
              </div>

              {scrapeStatus && (
                <div style={{ color: '#ddd', fontSize: '13px', marginTop: '5px' }}>
                  {scrapeStatus}
                </div>
              )}
              
              {/* Debug state display */}
              <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>
                Breakdown state: {scrapeBreakdown ? 'Present' : 'Missing'} 
                {scrapeBreakdown && ` (${Object.keys(scrapeBreakdown.providers || {}).length} providers)`}
              </div>
              
              {/* API Debug Info Display */}
              {scrapedData && (
                <div style={{ fontSize: '9px', color: '#888', marginTop: '3px', fontFamily: 'monospace' }}>
                  API Debug: Function called, Result type: object, Keys: {JSON.stringify(['data', 'summary', 'breakdown'])}
                </div>
              )}
              
              {/* Detailed provider breakdown */}
              {scrapeBreakdown && scrapeBreakdown.providers && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  background: 'rgba(76, 175, 80, 0.1)',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#ddd'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#4caf50' }}>
                    📊 Provider Breakdown:
                  </div>
                  {Object.values(scrapeBreakdown.providers).map(provider => (
                    <div key={provider.name} style={{ marginBottom: '6px' }}>
                      <span style={{
                        color: provider.status === 'success' ? '#4caf50' : '#f44336',
                        fontWeight: 'bold'
                      }}>
                        {provider.status === 'success' ? '✅' : '❌'} {provider.name}: {provider.movieCount} movies
                      </span>
                      {provider.urls && provider.urls.length > 1 && (
                        <div style={{ marginLeft: '20px', marginTop: '3px' }}>
                          {provider.urls.map((url, index) => (
                            <div key={index} style={{
                              color: url.status === 'success' ? '#81c784' : '#e57373',
                              fontSize: '11px'
                            }}>
                              • {url.path}: {url.movieCount} movies
                            </div>
                          ))}
                        </div>
                      )}
                      {provider.status === 'failed' && (
                        <div style={{
                          marginLeft: '20px',
                          marginTop: '2px',
                          color: '#ff9800',
                          fontSize: '10px',
                          fontStyle: 'italic'
                        }}>
                          Error: {provider.error || 'No specific error details'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Debug: Show raw breakdown data if present but no providers */}
              {scrapeBreakdown && !scrapeBreakdown.providers && (
                <div style={{
                  marginTop: '10px',
                  padding: '8px',
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#ffc107'
                }}>
                  🔍 Debug: Breakdown data received but no providers found
                  <pre style={{ margin: '5px 0', color: '#ddd' }}>
                    {JSON.stringify(scrapeBreakdown, null, 2).substring(0, 200)}...
                  </pre>
                </div>
              )}
            </div>

            {error ? (
              <div style={{ padding: '20px', background: '#2a0000', border: '1px solid #f44336', borderRadius: '8px' }}>
                <h3 style={{ color: '#f44336' }}>Error Loading Scraped Content</h3>
                <pre style={{ color: '#ff6b6b', overflow: 'auto' }}>{error.message}</pre>
              </div>
            ) : scrapedContent && Object.entries(scrapedContent).length > 0 ? (
              Object.entries(scrapedContent).map(([source, movies]) => (
                <div key={source} style={{
                  marginBottom: '30px',
                  border: '1px solid #444',
                  padding: '15px',
                  borderRadius: '8px',
                  background: '#1a1a1a'
                }}>
                  <h3>{source} <span style={{ color: '#999', fontSize: '0.9em' }}>({movies.length} movies)</span></h3>

                  {/* Debug info for sources with 0 movies */}
                  {movies.length === 0 && debugInfo && debugInfo[source] && (
                    <div style={{
                      background: 'rgba(255, 165, 0, 0.1)',
                      border: '1px solid rgba(255, 165, 0, 0.3)',
                      padding: '15px',
                      borderRadius: '5px',
                      marginTop: '10px'
                    }}>
                      <h4 style={{ color: '#ffa500', marginTop: 0 }}>🔍 Debug Information</h4>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                        {debugInfo[source].scrapedUrl && (
                          <p>🌐 <strong>Scraped URL:</strong> <a href={debugInfo[source].scrapedUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4a9eff' }}>{debugInfo[source].scrapedUrl}</a></p>
                        )}
                        {debugInfo[source].usedPuppeteer !== undefined && (
                          <p>🤖 <strong>Used Puppeteer:</strong> {debugInfo[source].usedPuppeteer ? 'Yes' : 'No'}</p>
                        )}
                        <p>✅ <strong>Fetch Successful:</strong> {debugInfo[source].fetchSuccess ? 'Yes' : 'No'}</p>
                        <p>📄 <strong>HTML Length:</strong> {debugInfo[source].htmlLength.toLocaleString()} bytes</p>
                        <p>🔍 <strong>Elements Found:</strong> {debugInfo[source].elementsFound}</p>
                        <p>📋 <strong>Movies Extracted:</strong> {debugInfo[source].moviesExtracted}</p>

                        {debugInfo[source].selectors && debugInfo[source].selectors.length > 0 && (
                          <div>
                            <p><strong>Selectors Tried:</strong></p>
                            <ul style={{ marginTop: '5px' }}>
                              {debugInfo[source].selectors.map((sel, idx) => (
                                <li key={idx}>{sel.selector}: {sel.count} matches</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {debugInfo[source].errors && debugInfo[source].errors.length > 0 && (
                          <div>
                            <p style={{ color: '#ff6b6b' }}>⚠️ <strong>Errors:</strong></p>
                            <ul style={{ marginTop: '5px', color: '#ff6b6b' }}>
                              {debugInfo[source].errors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {debugInfo[source].sampleElement && (
                          <details style={{ marginTop: '10px' }}>
                            <summary style={{ cursor: 'pointer', color: '#4a9eff' }}>
                              View Sample HTML Element
                            </summary>
                            <pre style={{
                              background: '#0a0a0a',
                              padding: '10px',
                              borderRadius: '4px',
                              overflow: 'auto',
                              fontSize: '0.85em',
                              marginTop: '10px'
                            }}>
                              {debugInfo[source].sampleElement}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Display movies grid */}
                  {movies.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '15px', marginTop: '15px' }}>
                      {movies.map(movie => (
                        <div key={movie.id} style={{
                          border: '1px solid #333',
                          padding: '10px',
                          borderRadius: '8px',
                          background: '#0a0a0a'
                        }}>
                          <img src={movie.thumbnailUrl} alt={movie.title} style={{ width: '100%', borderRadius: '4px' }} />
                          <h4 style={{ fontSize: '0.85em', marginTop: '8px', marginBottom: '4px' }}>{movie.title}</h4>
                          <p style={{ fontSize: '0.75em', color: '#999', margin: 0 }}>{movie.year}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>No scraped content available</p>
            )}
          </div>
        )}

        {/* Edit Movies Tab */}
        {activeTab === 'edit' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Edit Movie Metadata</h2>
              <button
                onClick={fetchEditMovies}
                style={{
                  padding: '10px 20px',
                  background: '#e50914',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Refresh
              </button>
            </div>

            {/* Genre Statistics in Edit Movies tab */}
            {genreLoading && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                Loading genre statistics...
              </div>
            )}
            
            {!genreLoading && renderGenreStatistics()}

            {saveStatus && (
              <div style={{
                padding: '10px 15px',
                marginBottom: '15px',
                background: saveStatus.includes('✅') ? '#0a3d0a' : '#3d0a0a',
                border: `1px solid ${saveStatus.includes('✅') ? '#28a745' : '#f44336'}`,
                borderRadius: '5px'
              }}>
                {saveStatus}
              </div>
            )}

            {editLoading && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Loading movies...</p>
              </div>
            )}

            {!editLoading && editMovies && editMovies.length > 0 && (
              <div style={{
                background: '#1a1a1a',
                borderRadius: '8px',
                border: '1px solid #444',
                overflow: 'hidden'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.9em'
                }}>
                  <thead>
                    <tr style={{ background: '#0a0a0a' }}>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #444', width: '180px' }}>Actions</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444', width: '25%' }}>Title & Year</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444', width: '20%' }}>Genres</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444', width: '80px' }}>Type</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444', width: '15%' }}>Source</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444', width: '35%' }}>Image URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editMovies.map(movie => (
                      <tr key={movie.id} style={{ borderBottom: '1px solid #333' }}>
                        {editingId === movie.id ? (
                          // Edit mode
                          <>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => saveEdit(movie.id)}
                                  style={{
                                    padding: '6px 12px',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85em'
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  style={{
                                    padding: '6px 12px',
                                    background: '#666',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85em'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <div>
                                <input
                                  type="text"
                                  value={editForm.title}
                                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                  placeholder="Enter movie title..."
                                  style={{
                                    padding: '6px 10px',
                                    background: '#333',
                                    color: 'white',
                                    border: '1px solid #555',
                                    borderRadius: '4px',
                                    width: '100%',
                                    fontWeight: 'bold',
                                    marginBottom: '5px'
                                  }}
                                />
                                <input
                                  type="number"
                                  value={editForm.year}
                                  onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}
                                  placeholder="Enter year..."
                                  min="1900"
                                  max={new Date().getFullYear()}
                                  style={{
                                    padding: '4px 8px',
                                    background: '#2a2a2a',
                                    color: '#999',
                                    border: '1px solid #555',
                                    borderRadius: '3px',
                                    width: '100px',
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>
                                <div style={{ fontSize: '0.85em', color: '#999' }}>{(movie.genre || []).join(', ')}</div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <select
                                value={editForm.type}
                                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                                style={{
                                  padding: '6px',
                                  background: '#333',
                                  color: 'white',
                                  border: '1px solid #555',
                                  borderRadius: '4px',
                                  width: '100%'
                                }}
                              >
                                <option value="movie">Movie</option>
                                <option value="tv_channel">TV Channel</option>
                              </select>
                            </td>
                            <td style={{ padding: '12px' }}>
                                <div style={{ fontSize: '0.85em', color: '#999' }}>{movie.scraped?.sources?.[0]?.sourceName || 'N/A'}</div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {isValidImageUrl(editForm.posterUrl) && (
                                  <img
                                    src={editForm.posterUrl}
                                    alt="Preview"
                                    style={{ width: '40px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                                    onError={(e) => { e.target.style.display = 'none' }}
                                  />
                                )}
                                <input
                                  type="text"
                                  value={editForm.posterUrl}
                                  onChange={(e) => setEditForm({ ...editForm, posterUrl: e.target.value })}
                                  placeholder="Enter image URL..."
                                  style={{
                                    flex: 1,
                                    padding: '6px 10px',
                                    background: '#333',
                                    color: 'white',
                                    border: '1px solid #555',
                                    borderRadius: '4px'
                                  }}
                                />
                              </div>
                            </td>
                          </>
                        ) : (
                          // View mode
                          <>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => handleRefreshMetadata(movie.id)}
                                  disabled={refreshingId === movie.id}
                                  style={{
                                    padding: '6px 12px',
                                    background: refreshingId === movie.id ? '#666' : '#4a9eff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: refreshingId === movie.id ? 'not-allowed' : 'pointer',
                                    fontSize: '0.85em'
                                  }}
                                >
                                  {refreshingId === movie.id ? '...' : 'Refresh'}
                                </button>
                                <button
                                  onClick={() => startEdit(movie)}
                                  style={{
                                    padding: '6px 16px',
                                    background: '#e50914',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85em'
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(movie)}
                                  style={{
                                    padding: '6px 16px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85em'
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <div>
                                <div style={{ fontWeight: 'bold' }}>{movie.title}</div>
                                <div style={{ fontSize: '0.85em', color: '#999' }}>{movie.metadata?.year || 'N/A'}</div>
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>
                                <div style={{ fontSize: '0.85em', color: '#999' }}>{(movie.genre || []).join(', ')}</div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <span style={{
                                padding: '4px 8px',
                                background: movie.type === 'tv_channel' ? '#4a9eff' : '#666',
                                borderRadius: '4px',
                                fontSize: '0.8em'
                              }}>
                                {movie.type || 'movie'}
                              </span>
                            </td>
                            <td style={{ padding: '12px' }}>
                                <div style={{ fontSize: '0.85em', color: '#999' }}>{movie.scraped?.sources?.[0]?.sourceName || 'N/A'}</div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {movie.metadata?.posterUrl ? (
                                  <>
                                    <img
                                      src={movie.metadata.posterUrl}
                                      alt={movie.title}
                                      style={{ width: '40px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                                      onError={(e) => { e.target.style.display = 'none' }}
                                    />
                                    <div style={{
                                      fontSize: '0.8em',
                                      color: '#999',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {movie.metadata.posterUrl}
                                    </div>
                                  </>
                                ) : (
                                  <span style={{ fontSize: '0.8em', color: '#666' }}>No image</span>
                                )}
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!editLoading && editMovies && editMovies.length > 0 && (
              <div style={{ padding: '20px', textAlign: 'center', display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {hasMoreMovies && (
                  <button
                    onClick={loadMoreMovies}
                    disabled={editLoading}
                    style={{
                      padding: '12px 24px',
                      background: editLoading ? '#666' : '#e50914',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: editLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    {editLoading ? 'Loading...' : `Load More Movies (${editMovies.length} loaded)`}
                  </button>
                )}
                
                <button
                  onClick={handleBatchRefreshFakeRatings}
                  disabled={batchRefreshing || editLoading}
                  style={{
                    padding: '12px 24px',
                    background: (batchRefreshing || editLoading) ? '#666' : '#4a9eff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: (batchRefreshing || editLoading) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {batchRefreshing 
                    ? `Fixing ${batchProgress.current}/${batchProgress.total}...` 
                    : `Fix Fake Ratings (${detectMoviesWithFakeRatings().length} found)`
                  }
                </button>
              </div>
            )}

            {/* Advanced Metadata Re-scraping */}
            {editMovies && editMovies.length > 0 && (
              <div style={{ 
                marginTop: '30px', 
                padding: '20px', 
                background: '#0a1a0a', 
                borderRadius: '8px',
                border: '2px solid #2d5a2d'
              }}>
                <h3 style={{ 
                  color: '#4caf50', 
                  marginBottom: '10px',
                  fontSize: '1.3em'
                }}>
                  🎯 Advanced Rating Fix (Recommended)
                </h3>
                <p style={{ 
                  color: '#b3b3b3', 
                  marginBottom: '15px',
                  lineHeight: '1.4'
                }}>
                  Intelligently identify and fix suspicious ratings by re-scraping from TMDB/OMDb.
                  Better than hiding content - fixes the data at the source.
                </p>

                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={handleScanSuspiciousRatings}
                    disabled={firebaseLoading || !firebaseMovies?.length || isFixingAll}
                    style={{
                      padding: '12px 24px',
                      background: firebaseLoading || isFixingAll ? '#666' : '#4a9eff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: firebaseLoading || isFixingAll ? 'default' : 'pointer',
                      fontSize: '0.9em',
                      fontWeight: 'bold',
                      marginRight: '10px',
                      marginBottom: '10px'
                    }}
                  >
                    🔍 Scan for Suspicious Ratings
                  </button>
                  
                  {suspiciousMovies.length > 0 && (
                    <button
                      onClick={handleBatchRefreshSuspicious}
                      disabled={isBatchRefreshing || isFixingAll}
                      style={{
                        padding: '12px 24px',
                        background: isBatchRefreshing || isFixingAll ? '#666' : '#ff6b35',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isBatchRefreshing || isFixingAll ? 'default' : 'pointer',
                        fontSize: '0.9em',
                        fontWeight: 'bold',
                        marginRight: '10px',
                        marginBottom: '10px'
                      }}
                    >
                      {isBatchRefreshing ? '🔄 Refreshing...' : `🚀 Fix ${suspiciousMovies.length} Movies`}
                    </button>
                  )}

                  {/* Fix ALL Button - scans entire database progressively */}
                  <button
                    onClick={handleFixAll}
                    disabled={isFixingAll || isBatchRefreshing}
                    style={{
                      padding: '15px 30px',
                      background: isFixingAll ? '#666' : 'linear-gradient(135deg, #e91e63, #9c27b0)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isFixingAll || isBatchRefreshing ? 'default' : 'pointer',
                      fontSize: '1em',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      boxShadow: isFixingAll ? 'none' : '0 4px 15px rgba(233, 30, 99, 0.4)',
                      transition: 'all 0.3s ease',
                      marginTop: '10px'
                    }}
                    onMouseOver={(e) => {
                      if (!isFixingAll && !isBatchRefreshing) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(233, 30, 99, 0.6)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isFixingAll && !isBatchRefreshing) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(233, 30, 99, 0.4)';
                      }
                    }}
                  >
                    {isFixingAll ? '🔄 Processing Database...' : '🚀 FIX ALL (Scan Entire Database)'}
                  </button>
                </div>

                {batchRefreshStatus && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '12px', 
                    background: batchRefreshStatus.includes('❌') ? 'rgba(255, 107, 53, 0.15)' : 'rgba(46, 211, 105, 0.15)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}>
                    {batchRefreshStatus}
                  </div>
                )}

                {/* Fix ALL Status Display */}
                {fixAllStatus && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: fixAllStatus.includes('❌') ? 'rgba(255, 107, 53, 0.2)' : 
                               fixAllStatus.includes('✅') ? 'rgba(76, 175, 80, 0.2)' : 
                               'rgba(233, 30, 99, 0.2)',
                    border: `2px solid ${fixAllStatus.includes('❌') ? '#ff6b35' : 
                                      fixAllStatus.includes('✅') ? '#4caf50' : '#e91e63'}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    color: '#fff',
                    whiteSpace: 'pre-line'
                  }}>
                    {fixAllStatus}
                    
                    {/* Progress indicators for Fix ALL */}
                    {fixAllProgress && isFixingAll && (
                      <div style={{ marginTop: '15px' }}>
                        <div style={{ marginBottom: '10px', fontSize: '12px', opacity: 0.8 }}>
                          Phase: {fixAllProgress.phase} | Total Movies: {fixAllProgress.results?.totalMovies || 'Calculating...'}
                        </div>
                        
                        {fixAllProgress.results && (
                          <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                            <div>📊 Scanned: {fixAllProgress.results.totalScanned}</div>
                            <div>⚠️ Suspicious: {fixAllProgress.results.totalSuspicious}</div>
                            <div>✅ Fixed: {fixAllProgress.results.totalFixed}</div>
                            <div>❌ Failed: {fixAllProgress.results.totalFailed}</div>
                            
                            {/* Phase progress bars */}
                            {Object.entries(fixAllProgress.results.phases).map(([phase, info]) => (
                              <div key={phase} style={{ marginTop: '8px' }}>
                                <div style={{ fontSize: '11px', marginBottom: '2px' }}>
                                  {phase.charAt(0).toUpperCase() + phase.slice(1)}: {info.completed ? '✅' : '🔄'}
                                </div>
                                <div style={{ 
                                  width: '100%', 
                                  height: '4px', 
                                  background: 'rgba(255,255,255,0.2)', 
                                  borderRadius: '2px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{ 
                                    width: `${info.progress || 0}%`, 
                                    height: '100%', 
                                    background: info.completed ? '#4caf50' : '#e91e63',
                                    transition: 'width 0.3s ease'
                                  }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {suspiciousMovies.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h4 style={{ color: '#ff6b35' }}>
                      Movies with Suspicious Ratings ({suspiciousMovies.length}):
                    </h4>
                    <div style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto', 
                      fontSize: '12px',
                      marginTop: '10px',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '4px'
                    }}>
                      {suspiciousMovies.slice(0, 20).map((movie, index) => (
                        <div key={movie.id} style={{ 
                          padding: '8px 0', 
                          borderBottom: index < 19 ? '1px solid #333' : 'none' 
                        }}>
                          <strong style={{ color: '#fff' }}>{movie.title}</strong>
                          <span style={{ color: '#ff6b35' }}> (Rating: {movie.rating}, Year: {movie.year || 'Missing'})</span>
                          <br />
                          <em style={{ color: '#999', fontSize: '11px' }}>
                            Issues: {movie.reasons.join(', ')}
                          </em>
                        </div>
                      ))}
                      {suspiciousMovies.length > 20 && (
                        <div style={{ 
                          padding: '10px', 
                          textAlign: 'center', 
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          ... and {suspiciousMovies.length - 20} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!editLoading && editMovies && editMovies.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', background: '#1a1a1a', borderRadius: '8px', border: '1px solid #444' }}>
                <p style={{ fontSize: '1.1em', marginBottom: '10px' }}>No movies in database yet</p>
                <p style={{ fontSize: '0.9em', color: '#999' }}>
                  Scrape and save movies using the "Scraped Content" tab first
                </p>
              </div>
            )}
          </div>
        )}

        {/* Provider URLs Tab */}
        {activeTab === 'providers' && (
          <div>
            <h2>Provider URLs</h2>
            <p style={{ color: '#999', marginBottom: '20px' }}>
              All configured scraping providers and their URLs. Click any URL to visit the source.
              <br />
              <strong>🎯 TubiTV now scrapes multiple URLs:</strong> /category/movies and /category/featured for more content variety!
            </p>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                background: '#1a1a1a',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <thead>
                  <tr style={{ background: '#333' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444' }}>Provider</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444' }}>Base URL</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444' }}>Movies URL</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #444' }}>TV Shows URL</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(editableProviders).map(([key, provider]) => (
                    <tr key={key} style={{ borderBottom: '1px solid #333' }}>
                      <td style={{ padding: '12px', fontWeight: 'bold' }}>{provider.name}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8em',
                          fontWeight: 'bold',
                          background: provider.type === 'Legal' ? '#28a745' : '#dc3545',
                          color: 'white'
                        }}>
                          {provider.type}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <a 
                          href={provider.baseUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            color: '#4a9eff', 
                            textDecoration: 'none',
                            fontSize: '0.9em'
                          }}
                          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {provider.baseUrl}
                        </a>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {provider.moviePath ? (
                          Array.isArray(provider.moviePath) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {provider.moviePath.map((path, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <a 
                                    href={provider.baseUrl + path} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ 
                                      color: '#4a9eff', 
                                      textDecoration: 'none',
                                      fontSize: '0.85em',
                                      flex: 1
                                    }}
                                    onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                  >
                                    {provider.baseUrl + path}
                                  </a>
                                  {provider.moviePath.length > 1 && (
                                    <button
                                      onClick={() => removeUrlFromProvider(key, index)}
                                      style={{
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        padding: '2px 6px',
                                        fontSize: '0.7em',
                                        cursor: 'pointer'
                                      }}
                                      title="Remove URL"
                                    >
                                      ✗
                                    </button>
                                  )}
                                </div>
                              ))}
                              
                              {/* Add URL interface */}
                              {editingProvider === key ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                                  <input
                                    type="text"
                                    value={newUrlPath}
                                    onChange={(e) => setNewUrlPath(e.target.value)}
                                    placeholder="/new/path"
                                    style={{
                                      background: '#333',
                                      border: '1px solid #555',
                                      color: 'white',
                                      padding: '4px 8px',
                                      fontSize: '0.8em',
                                      borderRadius: '3px',
                                      flex: 1
                                    }}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter' && newUrlPath.trim()) {
                                        addUrlToProvider(key, newUrlPath.trim());
                                        setEditingProvider(null);
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={() => {
                                      if (newUrlPath.trim()) {
                                        addUrlToProvider(key, newUrlPath.trim());
                                        setEditingProvider(null);
                                      }
                                    }}
                                    style={{
                                      background: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '3px',
                                      padding: '4px 8px',
                                      fontSize: '0.7em',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Add
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingProvider(null);
                                      setNewUrlPath('');
                                    }}
                                    style={{
                                      background: '#6c757d',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '3px',
                                      padding: '4px 8px',
                                      fontSize: '0.7em',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setEditingProvider(key)}
                                  style={{
                                    background: '#17a2b8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    padding: '4px 8px',
                                    fontSize: '0.7em',
                                    cursor: 'pointer',
                                    marginTop: '4px',
                                    alignSelf: 'flex-start'
                                  }}
                                >
                                  + Add URL
                                </button>
                              )}
                            </div>
                          ) : (
                            <a 
                              href={provider.baseUrl + provider.moviePath} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#4a9eff', 
                                textDecoration: 'none',
                                fontSize: '0.9em'
                              }}
                              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                            >
                              {provider.baseUrl + provider.moviePath}
                            </a>
                          )
                        ) : (
                          <span style={{ color: '#666', fontStyle: 'italic' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {provider.tvPath ? (
                          <a 
                            href={provider.baseUrl + provider.tvPath} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                              color: '#4a9eff', 
                              textDecoration: 'none',
                              fontSize: '0.9em'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {provider.baseUrl + provider.tvPath}
                          </a>
                        ) : (
                          <span style={{ color: '#666', fontStyle: 'italic' }}>N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              background: '#2a2a2a', 
              borderRadius: '6px',
              fontSize: '0.9em',
              color: '#ccc'
            }}>
              <strong>Note:</strong> Legal sources are free, ad-supported streaming services. 
              Piracy sources are for educational/research purposes only.
            </div>
          </div>
        )}

        {/* Firebase Movies Tab */}
        {activeTab === 'firebase' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Firebase Movies Database</h2>
              <button
                onClick={fetchFirebaseMovies}
                style={{
                  padding: '10px 20px',
                  background: '#e50914',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Refresh
              </button>
            </div>

            {firebaseLoading && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Loading Firebase movies...</p>
              </div>
            )}

            {firebaseError && (
              <div style={{
                padding: '20px',
                background: '#2a0000',
                border: '1px solid #f44336',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#f44336' }}>Error Loading Firebase Movies</h3>
                <pre style={{ color: '#ff6b6b', overflow: 'auto' }}>{firebaseError.message}</pre>
                <p style={{ marginTop: '10px', fontSize: '0.9em' }}>
                  Make sure you've enabled the Firestore API in your Firebase console.
                </p>
              </div>
            )}

            {!firebaseLoading && !firebaseError && firebaseMovies && (
              <>
                <div style={{ marginBottom: '20px', padding: '15px', background: '#1a1a1a', borderRadius: '8px', border: '1px solid #444' }}>
                  <p><strong>Total Movies:</strong> {firebaseMovies.length}</p>
                  <p style={{ fontSize: '0.9em', color: '#999', marginTop: '5px' }}>
                    Showing movies saved to Firestore from all scraping sources
                  </p>
                </div>

                {firebaseMovies.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', background: '#1a1a1a', borderRadius: '8px', border: '1px solid #444' }}>
                    <p style={{ fontSize: '1.1em', marginBottom: '10px' }}>No movies in database yet</p>
                    <p style={{ fontSize: '0.9em', color: '#999' }}>
                      Movies will appear here after they're scraped and saved to Firebase
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                    {firebaseMovies.map(movie => (
                      <div key={movie.id} style={{
                        border: '1px solid #333',
                        padding: '15px',
                        borderRadius: '8px',
                        background: '#1a1a1a'
                      }}>
                        {isValidImageUrl(movie.metadata?.posterUrl) && (
                          <img
                            src={movie.metadata.posterUrl}
                            alt={movie.title}
                            style={{ width: '100%', borderRadius: '4px', marginBottom: '10px' }}
                            onError={(e) => { e.target.style.display = 'none' }}
                          />
                        )}
                        <h3 style={{ fontSize: '1em', marginTop: 0, marginBottom: '5px' }}>{movie.title}</h3>
                        <p style={{ fontSize: '0.85em', color: '#999', margin: '5px 0' }}>
                          {movie.metadata?.year || 'Year unknown'}
                        </p>

                        {/* Sources */}
                        {movie.scraped?.sources && movie.scraped.sources.length > 0 && (
                          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                            <p style={{ fontSize: '0.75em', color: '#666', marginBottom: '5px' }}>SOURCES:</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                              {movie.scraped.sources.map((source, idx) => (
                                <span key={idx} style={{
                                  fontSize: '0.7em',
                                  padding: '3px 8px',
                                  background: '#333',
                                  borderRadius: '3px',
                                  color: '#4a9eff'
                                }}>
                                  {source.sourceName}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Ratings */}
                        {(movie.ratings?.imdbRating || movie.ratings?.rottenTomatoes || movie.ratings?.metacritic) && (
                          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #333' }}>
                            <p style={{ fontSize: '0.75em', color: '#666', marginBottom: '5px' }}>RATINGS:</p>
                            <div style={{ display: 'flex', gap: '10px', fontSize: '0.8em' }}>
                              {movie.ratings.imdbRating && (
                                <span>⭐ {movie.ratings.imdbRating}</span>
                              )}
                              {movie.ratings.rottenTomatoes && (
                                <span>🍅 {movie.ratings.rottenTomatoes}%</span>
                              )}
                              {movie.ratings.metacritic && (
                                <span>Ⓜ️ {movie.ratings.metacritic}</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Genres */}
                        {movie.genre && movie.genre.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <p style={{ fontSize: '0.75em', color: '#666', marginBottom: '5px' }}>GENRES:</p>
                            <p style={{ fontSize: '0.8em', color: '#aaa' }}>
                              {movie.genre.join(', ')}
                            </p>
                          </div>
                        )}

                        {/* Plot */}
                        {movie.metadata?.plot && (
                          <div style={{ marginTop: '10px' }}>
                            <p style={{ fontSize: '0.75em', color: '#666', marginBottom: '5px' }}>PLOT:</p>
                            <p style={{ fontSize: '0.75em', color: '#aaa', lineHeight: '1.4' }}>
                              {movie.metadata.plot.length > 150
                                ? movie.metadata.plot.substring(0, 150) + '...'
                                : movie.metadata.plot}
                            </p>
                          </div>
                        )}

                        {/* Timestamps */}
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #333', fontSize: '0.7em', color: '#666' }}>
                          {movie.scraped?.lastScrapedAt && (
                            <p style={{ margin: '2px 0' }}>
                              Last scraped: {new Date(movie.scraped.lastScrapedAt._seconds * 1000).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Genres Debug Tab */}
        {activeTab === 'genres' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Genre Debug Analysis</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleBackfillGenres}
                  disabled={isBackfilling}
                  style={{
                    padding: '10px 20px',
                    background: isBackfilling ? '#666' : '#4a9eff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: isBackfilling ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {isBackfilling ? 'Backfilling...' : 'Backfill Genres'}
                </button>
                <button
                  onClick={handleCleanupDuplicates}
                  disabled={isCleaningUp}
                  style={{
                    padding: '10px 20px',
                    background: isCleaningUp ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: isCleaningUp ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {isCleaningUp ? '🧹 Cleaning...' : '🧹 Cleanup Duplicates'}
                </button>
                <button
                  onClick={fetchGenreDebugData}
                  style={{
                    padding: '10px 20px',
                    background: '#e50914',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Refresh
                </button>
              </div>
            </div>

            {cleanupStatus && (
              <div style={{
                padding: '15px',
                marginBottom: '20px',
                background: cleanupStatus.includes('✅') ? '#0a3d0a' : cleanupStatus.includes('❌') ? '#3d0a0a' : '#1a1a1a',
                border: `1px solid ${cleanupStatus.includes('✅') ? '#28a745' : cleanupStatus.includes('❌') ? '#f44336' : '#444'}`,
                borderRadius: '8px',
                color: '#fff'
              }}>
                {cleanupStatus}
              </div>
            )}

            {backfillStatus && (
              <div style={{
                padding: '15px',
                marginBottom: '20px',
                background: backfillStatus.includes('✅') ? '#0a3d0a' : backfillStatus.includes('❌') ? '#3d0a0a' : '#1a1a1a',
                border: `1px solid ${backfillStatus.includes('✅') ? '#28a745' : backfillStatus.includes('❌') ? '#f44336' : '#444'}`,
                borderRadius: '8px',
                color: '#fff'
              }}>
                {backfillStatus}
              </div>
            )}

            {genreLoading && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Analyzing genre data...</p>
              </div>
            )}

            {!genreLoading && genreData && genreData.error && (
              <div style={{
                padding: '20px',
                background: '#2a0000',
                border: '1px solid #f44336',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{ color: '#f44336' }}>Error Loading Genre Data</h3>
                <pre style={{ color: '#ff6b6b', overflow: 'auto' }}>{genreData.error}</pre>
              </div>
            )}

            {!genreLoading && genreData && !genreData.error && (
              <>
                {/* Statistics Section */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px' }}>📊 Genre Statistics</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '15px'
                  }}>
                    <div style={{
                      background: '#1a1a1a',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#e50914' }}>
                        {genreData.total}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
                        Total Movies
                      </div>
                    </div>
                    <div style={{
                      background: '#1a1a1a',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#28a745' }}>
                        {genreData.stats.withGenres}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
                        With Genres
                      </div>
                    </div>
                    <div style={{
                      background: '#1a1a1a',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f44336' }}>
                        {genreData.stats.noGenres}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
                        No Genres
                      </div>
                    </div>
                    <div style={{
                      background: '#1a1a1a',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#4a9eff' }}>
                        {genreData.stats.withRootGenre}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
                        Has 'genre' Field
                      </div>
                    </div>
                    <div style={{
                      background: '#1a1a1a',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ffa500' }}>
                        {genreData.stats.withMetadataGenres}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
                        Has 'metadata.genres'
                      </div>
                    </div>
                    <div style={{
                      background: '#1a1a1a',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ff6b6b' }}>
                        {genreData.stats.withEmptyGenreArray}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#999', marginTop: '8px' }}>
                        Empty genre[] Arrays
                      </div>
                    </div>
                  </div>
                </div>

                {/* Genre Distribution */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px' }}>📈 Genre Distribution (Top 20)</h3>
                  <div style={{
                    background: '#1a1a1a',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #444'
                  }}>
                    {genreData.genreCounts.length > 0 ? (
                      genreData.genreCounts.slice(0, 20).map(([genre, count]) => (
                        <div key={genre} style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '12px',
                          gap: '15px'
                        }}>
                          <div style={{ minWidth: '150px', fontWeight: 'bold' }}>
                            {genre}
                          </div>
                          <div style={{ flex: 1, position: 'relative', height: '24px' }}>
                            <div style={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              height: '100%',
                              background: 'linear-gradient(90deg, #e50914, #ff4444)',
                              width: `${(count / genreData.total) * 100}%`,
                              borderRadius: '4px',
                              transition: 'width 0.3s'
                            }}></div>
                          </div>
                          <div style={{ minWidth: '80px', textAlign: 'right', color: '#999' }}>
                            {count} movies
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                        No genre data found
                      </p>
                    )}
                  </div>
                </div>

                {/* Sample Data */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px' }}>🔍 Sample Movies (First 15)</h3>
                  <div style={{
                    background: '#1a1a1a',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #444'
                  }}>
                    {genreData.samples.map((sample, idx) => (
                      <div key={sample.id || idx} style={{
                        marginBottom: '20px',
                        paddingBottom: '20px',
                        borderBottom: idx < genreData.samples.length - 1 ? '1px solid #333' : 'none'
                      }}>
                        <div style={{ marginBottom: '10px', fontSize: '1.1em', fontWeight: 'bold' }}>
                          {idx + 1}. {sample.title}
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '15px'
                        }}>
                          <div style={{
                            background: sample.hasRootGenre ? 'rgba(40, 167, 69, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                            border: `1px solid ${sample.hasRootGenre ? '#28a745' : '#f44336'}`,
                            padding: '12px',
                            borderRadius: '6px'
                          }}>
                            <div style={{
                              fontSize: '0.8em',
                              color: '#999',
                              marginBottom: '8px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span>genre:</span>
                              <span style={{
                                color: sample.hasRootGenre ? '#28a745' : '#f44336',
                                fontWeight: 'bold'
                              }}>
                                {sample.hasRootGenre ? '✓' : (sample.hasEmptyRootGenre ? '[] EMPTY' : '✗ MISSING')}
                              </span>
                            </div>
                            <div style={{
                              fontFamily: 'monospace',
                              fontSize: '0.85em',
                              color: sample.hasRootGenre ? '#4caf50' : '#ff6b6b',
                              wordBreak: 'break-all'
                            }}>
                              {JSON.stringify(sample.genre)}
                            </div>
                          </div>
                          <div style={{
                            background: sample.hasMetadataGenres ? 'rgba(40, 167, 69, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                            border: `1px solid ${sample.hasMetadataGenres ? '#28a745' : '#f44336'}`,
                            padding: '12px',
                            borderRadius: '6px'
                          }}>
                            <div style={{
                              fontSize: '0.8em',
                              color: '#999',
                              marginBottom: '8px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span>metadata.genres:</span>
                              <span style={{
                                color: sample.hasMetadataGenres ? '#28a745' : '#f44336',
                                fontWeight: 'bold'
                              }}>
                                {sample.hasMetadataGenres ? '✓' : '✗'}
                              </span>
                            </div>
                            <div style={{
                              fontFamily: 'monospace',
                              fontSize: '0.85em',
                              color: sample.hasMetadataGenres ? '#4caf50' : '#ff6b6b',
                              wordBreak: 'break-all'
                            }}>
                              {JSON.stringify(sample.metadataGenres)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Diagnosis Section */}
                <div style={{
                  background: 'rgba(255, 165, 0, 0.1)',
                  border: '1px solid rgba(255, 165, 0, 0.3)',
                  padding: '20px',
                  borderRadius: '8px',
                  marginTop: '20px'
                }}>
                  <h3 style={{ color: '#ffa500', marginTop: 0 }}>🔍 Diagnosis</h3>
                  <div style={{ fontSize: '0.9em', lineHeight: '1.6' }}>
                    {genreData.stats.withEmptyGenreArray > 0 && (
                      <p style={{ color: '#ff6b6b' }}>
                        ⚠️ <strong>Issue Found:</strong> {genreData.stats.withEmptyGenreArray} movies have empty <code>genre[]</code> arrays.
                        This causes the filtering to fail because empty arrays are truthy in JavaScript.
                      </p>
                    )}
                    {genreData.stats.withMetadataGenres > genreData.stats.withRootGenre && (
                      <p style={{ color: '#ffa500' }}>
                        ⚠️ <strong>Data Mismatch:</strong> More movies have <code>metadata.genres</code> ({genreData.stats.withMetadataGenres})
                        than <code>genre</code> ({genreData.stats.withRootGenre}). The transformation in apiService.js may not be working correctly.
                      </p>
                    )}
                    {genreData.stats.withRootGenre === 0 && genreData.stats.withMetadataGenres > 0 && (
                      <p style={{ color: '#f44336' }}>
                        ❌ <strong>Critical Issue:</strong> NO movies have the <code>genre</code> field, but {genreData.stats.withMetadataGenres} have <code>metadata.genres</code>.
                        The frontend filter expects <code>genre</code> field. Check apiService.js transformation!
                      </p>
                    )}
                    {genreData.genreCounts.length > 0 && (
                      <p style={{ color: '#4caf50' }}>
                        ✅ Genre data exists in the database. Most common: {genreData.genreCounts.slice(0, 3).map(([g]) => g).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Debug;
